import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/shadcn/button";
import UpdateAwareErrorDisplay from "@/components/ui/UpdateAwareErrorDisplay";
import { reportError } from "@/lib/analytics";
import i18n from "@/i18n";

/**
 * Dernier filet de l'application. Sans lui, n'importe quel throw pendant le
 * render — y compris celui d'un `useQuery` Convex dont la query échoue côté
 * serveur, qui throw pendant le render par design — démonte tout l'arbre React
 * et laisse une page blanche.
 *
 * Il est monté au-dessus du `RouterProvider` dans `main.tsx`. Les erreurs
 * survenant *dans* une route sont d'abord attrapées par l'`errorComponent` du
 * routeur (cf. `src/routes/__root.tsx`), qui préserve la coquille de l'app ;
 * celui-ci ne sert que pour ce qui casse au-dessus ou à côté du routeur.
 */
interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  error: Error | null;
}

export class AppErrorBoundary extends Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportError(error, {
      source: "AppErrorBoundary",
      componentStack: info.componentStack,
    });
  }

  // Navigation dure plutôt que routeur : quand ce boundary rend, l'arbre React
  // est déjà démonté, donc rien ne garantit qu'un `navigate` remonte une app
  // saine. Un chargement complet repart d'un état propre.
  handleBackHome = (): void => {
    window.location.assign("/");
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="h-screen w-screen">
        {/* Reload = build frais plutôt que précache : l'erreur peut venir d'un
            onglet resté sur un build décalé du backend (cf. `applyUpdate`).
            `autoReload` : l'app est déjà démontée, rien à perdre. */}
        <UpdateAwareErrorDisplay
          title={i18n.t("common:errors.generic")}
          message={i18n.t("shell:appError.message")}
          autoReload
          secondaryActions={
            <Button variant="outline" onClick={this.handleBackHome}>
              {i18n.t("shell:root.backToCanvases")}
            </Button>
          }
        />
      </div>
    );
  }
}
